import { createStore } from 'zustand'
import type { StoreApi } from 'zustand'

/**
 * One entry in the list's navigation order. `isRepliable` is supplied by each
 * list and captures the card's *intrinsic* eligibility for auto-expansion and
 * `onlyRepliable` navigation — repliable and not already resolved on the
 * server. Comments derive it from status alone; mentions additionally require
 * channel-level repliability (notify-only cards are never repliable). The store
 * layers this session's resolutions on top (see `isCandidate`), so neither list
 * has to re-subtract `locallyResolved` itself — that subtraction lived in two
 * places before and is the kind of thing that drifts. Folding the per-feature
 * predicate down to a flag keeps the store's navigation maths feature-agnostic.
 */
export type CoordinationItem = {
  id: string
  isRepliable: boolean
}

/**
 * The symmetric cross-card coordination shared by the Comments and Mentions
 * lists: which single card is expanded, the navigation order, and the set of
 * cards resolved (replied/dismissed) this session before the server catches up.
 *
 * Feature-specific list concerns (by-post, dismiss-all, counts, Pusher, the
 * empty-state "all resolved" derivation that needs server status) deliberately
 * stay out of this store — see the list components and the wiring hook.
 */
export type EngagementCoordinationState = {
  /** The single expanded card, or `null` when everything is collapsed. */
  expandedId: string | null
  /** Visible cards in render order, with their candidacy for auto-expansion. */
  order: ReadonlyArray<CoordinationItem>
  /** Cards resolved (replied/dismissed) this session. */
  locallyResolved: ReadonlySet<string>
  /** Cards explicitly un-resolved this session (e.g. undo dismiss), so a
   *  server-resolved status doesn't override the local intent. */
  locallyUnresolved: ReadonlySet<string>
  /** Cards that arrived via the "New comments available" pill this session.
   *  They render above the "New comments" divider until the list identity
   *  (channel/post/status/sort/date) changes. */
  newlyArrivedIds: ReadonlySet<string>
}

export type EngagementCoordinationActions = {
  /**
   * Sync the navigation order + candidacy. Also prunes `expandedId` when the
   * expanded card has left the list (the old per-list "clear when gone"
   * effect), keeping that behaviour pure and testable.
   */
  setOrder: (items: ReadonlyArray<CoordinationItem>) => void
  /**
   * Manually expand a card (or collapse with `null`). Ignores ids absent from
   * the current order so a stale click can't expand a phantom card.
   */
  expand: (id: string | null) => void
  /** Collapse the expanded card. */
  collapse: () => void
  /**
   * Expand the card after `fromId` (defaulting to the expanded card). With
   * `onlyRepliable`, skips non-candidate cards (used after a resolution);
   * without it, steps to the literal next card (arrow-key navigation).
   * Collapses when there is nothing to advance to. No-op on an empty list.
   */
  expandNext: (fromId?: string, onlyRepliable?: boolean) => void
  /**
   * Expand the card before `fromId` (defaulting to the expanded card).
   * Collapses at the top of the list. No-op on an empty list.
   */
  expandPrevious: (fromId?: string) => void
  /** Mark a card resolved/un-resolved this session. */
  setResolution: (id: string, isResolved: boolean) => void
  /**
   * The reply flow's auto-advance: collapse the just-resolved card and open the
   * next repliable one. Identical to `expandNext(fromId, true)` — the reply form
   * owns the timing (the post-submit delay + cancel-on-error); the store owns
   * which card comes next.
   */
  resolveAndAdvance: (fromId: string) => void
  /** Collapse everything (filter/channel change). */
  resetExpansion: () => void
  /** Clear the session resolution sets (filter/channel change). */
  resetResolution: () => void
  /** Add ids to the newly-arrived set. Accumulates across pill clicks so
   *  comments from an earlier click stay above the divider. */
  addNewlyArrived: (ids: readonly string[]) => void
  /** Clear the newly-arrived set. */
  clearNewlyArrived: () => void
}

export type EngagementCoordinationStoreState = EngagementCoordinationState &
  EngagementCoordinationActions

/**
 * Where the next expansion should land, given the current order and a reference
 * card. Pure so both the store action and the Comments scroll-and-expand
 * decorator can share the exact same next-card maths.
 *
 * - `noop`: empty list — leave expansion untouched.
 * - `collapse`: no reference, reference missing, at/past the last card, or no
 *   candidate follows under `onlyRepliable`.
 * - `expand`: the id of the card to open next.
 */
export type NextExpansion =
  | { type: 'noop' }
  | { type: 'collapse' }
  | { type: 'expand'; id: string }

/**
 * Whether a card is a live target for auto-expansion / `onlyRepliable`
 * navigation: intrinsically repliable AND not resolved this session. The store
 * owns this single definition so the two lists can't disagree on whether a
 * card resolved this session still counts (it never does).
 */
export const isCandidate = (
  item: CoordinationItem,
  locallyResolved: ReadonlySet<string>,
): boolean => item.isRepliable && !locallyResolved.has(item.id)

export const resolveNextExpansion = (
  order: ReadonlyArray<CoordinationItem>,
  referenceId: string | null | undefined,
  onlyRepliable: boolean,
  locallyResolved: ReadonlySet<string>,
): NextExpansion => {
  if (order.length === 0) return { type: 'noop' }
  if (!referenceId) return { type: 'collapse' }

  const currentIndex = order.findIndex((item) => item.id === referenceId)
  if (currentIndex === -1 || currentIndex + 1 >= order.length) {
    return { type: 'collapse' }
  }

  const next = onlyRepliable
    ? order
        .slice(currentIndex + 1)
        .find((item) => isCandidate(item, locallyResolved))
    : order[currentIndex + 1]

  return next ? { type: 'expand', id: next.id } : { type: 'collapse' }
}

const withAdded = (set: ReadonlySet<string>, id: string): Set<string> => {
  const next = new Set(set)
  next.add(id)
  return next
}

const withRemoved = (set: ReadonlySet<string>, id: string): Set<string> => {
  const next = new Set(set)
  next.delete(id)
  return next
}

export type EngagementCoordinationStore =
  StoreApi<EngagementCoordinationStoreState>

/**
 * Creates a per-list coordination store. Each list (Comments, Mentions) owns
 * its own instance via the coordination context; cards subscribe with
 * selectors so a navigation change re-renders only the cards whose expanded
 * state actually flipped.
 */
export const createEngagementCoordinationStore =
  (): EngagementCoordinationStore =>
    createStore<EngagementCoordinationStoreState>()((set, get) => ({
      expandedId: null,
      order: [],
      locallyResolved: new Set<string>(),
      locallyUnresolved: new Set<string>(),
      newlyArrivedIds: new Set<string>(),

      setOrder: (items): void => {
        const { expandedId } = get()
        const expandedStillPresent =
          expandedId !== null && items.some((item) => item.id === expandedId)
        set({
          order: items,
          expandedId: expandedStillPresent ? expandedId : null,
        })
      },

      expand: (id): void => {
        if (id === null) {
          set({ expandedId: null })
          return
        }
        if (!get().order.some((item) => item.id === id)) return
        set({ expandedId: id })
      },

      collapse: (): void => set({ expandedId: null }),

      expandNext: (fromId, onlyRepliable = false): void => {
        const { order, expandedId, locallyResolved } = get()
        const result = resolveNextExpansion(
          order,
          fromId ?? expandedId,
          onlyRepliable,
          locallyResolved,
        )
        if (result.type === 'noop') return
        set({ expandedId: result.type === 'expand' ? result.id : null })
      },

      expandPrevious: (fromId): void => {
        const { order, expandedId } = get()
        if (order.length === 0) return
        const currentIndex = order.findIndex(
          (item) => item.id === (fromId ?? expandedId),
        )
        if (currentIndex <= 0) {
          set({ expandedId: null })
          return
        }
        set({ expandedId: order[currentIndex - 1].id })
      },

      setResolution: (id, isResolved): void => {
        const { locallyResolved, locallyUnresolved } = get()
        if (isResolved) {
          set({
            locallyResolved: withAdded(locallyResolved, id),
            locallyUnresolved: withRemoved(locallyUnresolved, id),
          })
        } else {
          set({
            locallyResolved: withRemoved(locallyResolved, id),
            locallyUnresolved: withAdded(locallyUnresolved, id),
          })
        }
      },

      resolveAndAdvance: (fromId): void => {
        get().expandNext(fromId, true)
      },

      // The filter/channel reset call site. Collapses (delegating to collapse so
      // the two can't drift) and clears the newly-arrived boundary, since a new
      // list identity means the "New comments" divider no longer applies.
      resetExpansion: (): void => {
        get().collapse()
        get().clearNewlyArrived()
      },

      resetResolution: (): void =>
        set({
          locallyResolved: new Set<string>(),
          locallyUnresolved: new Set<string>(),
        }),

      addNewlyArrived: (ids): void => {
        if (ids.length === 0) return
        set({ newlyArrivedIds: new Set([...get().newlyArrivedIds, ...ids]) })
      },

      clearNewlyArrived: (): void =>
        set({ newlyArrivedIds: new Set<string>() }),
    }))

// ========================================
// Selectors — granular reads for per-card subscriptions
// ========================================

export const selectExpandedId = (
  state: EngagementCoordinationStoreState,
): string | null => state.expandedId

export const selectIsExpanded =
  (id: string) =>
  (state: EngagementCoordinationStoreState): boolean =>
    state.expandedId === id

export const selectIsLocallyResolved =
  (id: string) =>
  (state: EngagementCoordinationStoreState): boolean =>
    state.locallyResolved.has(id)

export const selectLocallyResolved = (
  state: EngagementCoordinationStoreState,
): ReadonlySet<string> => state.locallyResolved

export const selectLocallyUnresolved = (
  state: EngagementCoordinationStoreState,
): ReadonlySet<string> => state.locallyUnresolved

export const selectNewlyArrivedIds = (
  state: EngagementCoordinationStoreState,
): ReadonlySet<string> => state.newlyArrivedIds

// ========================================
// Empty-state derivation (feature-specific consumer: Comments)
// ========================================

/**
 * Whether every visible card counts as resolved — used to swap the list for a
 * celebratory empty state. A card is resolved when it was marked this session
 * OR its server status is already resolved, unless it was explicitly
 * un-resolved this session (e.g. undo dismiss). Requires at least one local
 * resolution so the empty state never triggers on initial load when everything
 * happens to already be resolved.
 *
 * Lives here (next to the resolution sets) but is consumed only by the Comments
 * list; it needs each card's server-resolved status, which the store doesn't
 * hold, so callers pass it in alongside the store's resolution sets.
 */
export const deriveAllResolved = (
  items: ReadonlyArray<{ id: string; isResolvedOnServer: boolean }>,
  locallyResolved: ReadonlySet<string>,
  locallyUnresolved: ReadonlySet<string>,
): boolean => {
  if (items.length === 0) return false
  if (locallyResolved.size === 0) return false
  return items.every((item) => {
    if (locallyUnresolved.has(item.id)) return false
    return locallyResolved.has(item.id) || item.isResolvedOnServer
  })
}
